import { Box, Drawer, IconButton, useTheme } from "@mui/material";
import { CloseIcon } from "components";
import {
  useCreateOnboarding,
  useGetOnboarding,
  useSetMspFeatureFlagAsSeen,
} from "controller";
import { useCallback, useEffect, useMemo, useState } from "react";
import { getMspId, getResourceId } from "utilz/helpers/CredentialData";
import {
  type AnalyticsEventKeys,
  addMixpanelEvent,
} from "utilz/helpers/mixpanelLogger";
import {
  DRAWER_SEEN_STAGE_KEY,
  DRAWER_SEEN_STAGE_NUMBER,
  DRAWER_STEPS,
} from "./drawerSteps";
import { OnboardingDrawerContent } from "./OnboardingDrawerContent";
import { OnboardingDrawerSidebar } from "./OnboardingDrawerSidebar";
import type { DrawerStep, DrawerStepKey } from "./types";

const SIDEBAR_EVENTS: Record<DrawerStepKey, AnalyticsEventKeys> = {
  welcome: "onb_sidebar_signedup_clicked",
  connect_support_mail: "onb_sidebar_supportmail_clicked",
  team: "onb_sidebar_team_clicked",
  branding: "onb_sidebar_branding_clicked",
  customers: "onb_sidebar_customers_clicked",
  channels: "onb_sidebar_apps_clicked",
  integrations: "onb_sidebar_rmm_clicked",
};

const COMPLETED_EVENTS: Record<DrawerStepKey, AnalyticsEventKeys> = {
  welcome: "onb_welcome_completed",
  connect_support_mail: "onb_connect_support_mail_completed",
  team: "onb_add_teammates_completed",
  branding: "onb_branding_completed",
  customers: "onb_create_customer_completed",
  channels: "onb_download_apps_completed",
  integrations: "onb_connect_rmm_completed",
};

const getVisitedStorageKey = (mspId: string, userId: string) =>
  `onb_drawer_visited:${mspId}:${userId}`;

const getLockClaimedStorageKey = (mspId: string, userId: string) =>
  `onb_drawer_lock_claimed:${mspId}:${userId}`;

interface OnboardingDrawerProps {
  open: boolean;
  onClose: () => void;
}

export const OnboardingDrawer = ({ open, onClose }: OnboardingDrawerProps) => {
  const theme = useTheme();
  const [activeStepKey, setActiveStepKey] = useState<DrawerStepKey>(
    DRAWER_STEPS[0].key,
  );

  const { data: onboardingData, isLoading: isOnboardingLoading } =
    useGetOnboarding();
  const { mutateAsync: createOnboarding, isLoading: isCreateLoading } =
    useCreateOnboarding();
  const { mutateAsync: setMspFeatureAsSeen } = useSetMspFeatureFlagAsSeen();

  const completedStepKeys = useMemo(() => {
    const setupSequence = onboardingData?.setupSequence || [];
    const completed = new Set<DrawerStepKey>();

    for (const step of DRAWER_STEPS) {
      const apiStep = setupSequence.find(
        (s: { stage_key: string; is_completed: boolean }) =>
          s.stage_key === step.key,
      );
      if (apiStep?.is_completed) {
        completed.add(step.key);
      }
    }

    return completed;
  }, [onboardingData?.setupSequence]);

  // First-time detection.
  // The DRAWER_SEEN_STAGE_KEY flag is frontend-set, so it's absent for BOTH
  // new and pre-existing users. We need a second signal to distinguish them:
  // completion of any non-welcome drawer step (welcome is auto-completed for
  // every user on modal close, so it's not a signal of drawer engagement).
  //
  // A localStorage "lock claimed" marker plugs the bypass where a first-time
  // user marks one step complete and force-closes the tab — that completion
  // would otherwise flip `hasEngagedWithDrawer` to true on next visit and
  // free them. Once we've identified a user as first-time, we keep them
  // locked across sessions until the drawer_seen flag is written.
  //
  // First-time = drawer_seen flag is NOT true AND (no non-welcome step
  // completed OR lock was previously claimed on this browser).
  // While loading, treat as first-time so we never let them close prematurely.
  const storageKey = useMemo(
    () => getVisitedStorageKey(getMspId(), getResourceId()),
    [],
  );
  const lockClaimedKey = useMemo(
    () => getLockClaimedStorageKey(getMspId(), getResourceId()),
    [],
  );

  const [isLockClaimed, setIsLockClaimed] = useState(() => {
    try {
      return localStorage.getItem(lockClaimedKey) === "true";
    } catch {
      return false;
    }
  });

  const drawerSeenStage = onboardingData?.setupSequence?.find(
    (s: { stage_key: string; is_completed: boolean }) =>
      s.stage_key === DRAWER_SEEN_STAGE_KEY,
  );
  const hasEngagedWithDrawer = [...completedStepKeys].some(
    (key) => key !== "welcome",
  );
  const isFirstTimeInDrawer = isOnboardingLoading
    ? true
    : drawerSeenStage?.is_completed !== true &&
      (!hasEngagedWithDrawer || isLockClaimed);

  // Claim the lock as soon as a user is identified as first-time so that a
  // mid-flow tab close + one-step-completed cannot bypass on next visit.
  useEffect(() => {
    if (!isFirstTimeInDrawer || isLockClaimed) return;
    try {
      localStorage.setItem(lockClaimedKey, "true");
    } catch {
      // quota / privacy mode — silently drop persistence
    }
    setIsLockClaimed(true);
  }, [isFirstTimeInDrawer, isLockClaimed, lockClaimedKey]);

  const [visitedStepKeys, setVisitedStepKeys] = useState<Set<DrawerStepKey>>(
    () => {
      const base = new Set<DrawerStepKey>([DRAWER_STEPS[0].key]);
      try {
        const stored = localStorage.getItem(storageKey);
        if (stored) {
          const parsed = JSON.parse(stored) as DrawerStepKey[];
          if (Array.isArray(parsed)) {
            parsed.forEach((k) => {
              base.add(k);
            });
          }
        }
      } catch {
        // corrupted entry — ignore
      }
      return base;
    },
  );

  // True once the user has navigated AWAY from the active step and back —
  // i.e., it was already in visitedStepKeys before the current navigation.
  // Drives Skip→Next label swap and Skip-button hide on the last step.
  const [isActiveStepRevisit, setIsActiveStepRevisit] = useState(false);

  useEffect(() => {
    if (!isFirstTimeInDrawer) return;
    try {
      localStorage.setItem(
        storageKey,
        JSON.stringify(Array.from(visitedStepKeys)),
      );
    } catch {
      // quota or privacy mode — silently drop persistence
    }
  }, [visitedStepKeys, isFirstTimeInDrawer, storageKey]);

  // Sidebar-click unlock rule: a step is clickable iff its immediate-previous
  // step (in DRAWER_STEPS array order) is already visited. Returning users
  // (not first-time) have everything unlocked.
  const unlockedStepKeys = useMemo(() => {
    if (!isFirstTimeInDrawer) {
      return new Set<DrawerStepKey>(DRAWER_STEPS.map((s) => s.key));
    }
    const unlocked = new Set<DrawerStepKey>();
    for (let i = 0; i < DRAWER_STEPS.length; i++) {
      if (i === 0 || visitedStepKeys.has(DRAWER_STEPS[i - 1].key)) {
        unlocked.add(DRAWER_STEPS[i].key);
      }
    }
    return unlocked;
  }, [visitedStepKeys, isFirstTimeInDrawer]);

  const allVisited = visitedStepKeys.size === DRAWER_STEPS.length;
  const isCloseButtonDisabled = isFirstTimeInDrawer && !allVisited;

  const handleStepClick = useCallback(
    (key: DrawerStepKey) => {
      // Defensive: ignore clicks on locked steps even if UI somehow allows it
      if (isFirstTimeInDrawer && !unlockedStepKeys.has(key)) return;
      addMixpanelEvent({ event: SIDEBAR_EVENTS[key] });
      setActiveStepKey(key);
      setIsActiveStepRevisit(visitedStepKeys.has(key));
      setVisitedStepKeys((prev) => {
        if (prev.has(key)) return prev;
        const next = new Set(prev);
        next.add(key);
        return next;
      });
    },
    [isFirstTimeInDrawer, unlockedStepKeys, visitedStepKeys],
  );

  // Programmatic close shared by the X button, Skip-on-last-step, and
  // Mark-Complete auto-close paths. Handles first-time bookkeeping and
  // optionally hides the top-bar onboarding entry point.
  const performClose = useCallback(
    async ({ flipShowOnboardingFlag }: { flipShowOnboardingFlag: boolean }) => {
      if (isFirstTimeInDrawer) {
        try {
          await createOnboarding({
            stage_number: DRAWER_SEEN_STAGE_NUMBER,
            stage_key: DRAWER_SEEN_STAGE_KEY,
            is_completed: true,
          });
          try {
            localStorage.removeItem(storageKey);
            localStorage.removeItem(lockClaimedKey);
          } catch {
            // ignore
          }
          setIsLockClaimed(false);
        } catch {
          // hook's onError logs; close optimistically
        }
      }
      if (flipShowOnboardingFlag) {
        try {
          await setMspFeatureAsSeen("show_onboarding_button");
        } catch {
          // ignore
        }
      }
      onClose();
    },
    [
      createOnboarding,
      isFirstTimeInDrawer,
      lockClaimedKey,
      onClose,
      setMspFeatureAsSeen,
      storageKey,
    ],
  );

  const handleSkip = (currentStep: DrawerStep) => {
    const currentIndex = DRAWER_STEPS.findIndex(
      (s) => s.key === currentStep.key,
    );
    const isLastStep = currentIndex === DRAWER_STEPS.length - 1;
    if (isLastStep) {
      // Mark last step visited so the first-time close gate passes.
      setVisitedStepKeys((prev) => {
        if (prev.has(currentStep.key)) return prev;
        const next = new Set(prev);
        next.add(currentStep.key);
        return next;
      });
      const allStepsComplete = completedStepKeys.size === DRAWER_STEPS.length;
      void performClose({ flipShowOnboardingFlag: allStepsComplete });
      return;
    }
    const nextStep = DRAWER_STEPS[currentIndex + 1];
    if (!nextStep) return;
    handleStepClick(nextStep.key);
  };

  const handleMarkComplete = useCallback(
    async (step: DrawerStep) => {
      const currentIndex = DRAWER_STEPS.findIndex((s) => s.key === step.key);
      const isLastStep = currentIndex === DRAWER_STEPS.length - 1;
      const willCompleteAllSteps =
        completedStepKeys.size === DRAWER_STEPS.length - 1 &&
        !completedStepKeys.has(step.key);
      try {
        await createOnboarding({
          stage_number: step.stageNumber,
          stage_key: step.key,
          is_completed: true,
        });
        addMixpanelEvent({ event: COMPLETED_EVENTS[step.key] });
        if (willCompleteAllSteps) {
          addMixpanelEvent({ event: "onb_drawer_completed" });
          // Trigger is "all steps complete in backend" — not "last step in
          // DRAWER_STEPS order". A user can complete steps out of order, so
          // any step can be the one that finishes the flow.
          await performClose({ flipShowOnboardingFlag: true });
          return;
        }
        if (isLastStep) {
          // No next step to advance to and not all are complete → stay put.
          return;
        }
        const nextStep = DRAWER_STEPS[currentIndex + 1];
        handleStepClick(nextStep.key);
      } catch {
        // Error handled by hook's onError
      }
    },
    [completedStepKeys, createOnboarding, handleStepClick, performClose],
  );

  const [isMarkingAll, setIsMarkingAll] = useState(false);

  const handleMarkAllComplete = useCallback(async () => {
    setIsMarkingAll(true);
    try {
      const incompleteSteps = DRAWER_STEPS.filter(
        (step) => !completedStepKeys.has(step.key),
      );
      await Promise.all(
        incompleteSteps.map((step) =>
          createOnboarding({
            stage_number: step.stageNumber,
            stage_key: step.key,
            is_completed: true,
          }),
        ),
      );
      // Also flip the meta stage so user is no longer first-time.
      if (isFirstTimeInDrawer) {
        await createOnboarding({
          stage_number: DRAWER_SEEN_STAGE_NUMBER,
          stage_key: DRAWER_SEEN_STAGE_KEY,
          is_completed: true,
        });
        try {
          localStorage.removeItem(storageKey);
          localStorage.removeItem(lockClaimedKey);
        } catch {
          // ignore
        }
        setIsLockClaimed(false);
      }
      addMixpanelEvent({ event: "onb_marked_all_complete" });
      addMixpanelEvent({ event: "onb_drawer_completed" });
      await setMspFeatureAsSeen("show_onboarding_button");
    } catch {
      // Error handled by hook's onError
    } finally {
      setIsMarkingAll(false);
    }
  }, [
    completedStepKeys,
    createOnboarding,
    isFirstTimeInDrawer,
    lockClaimedKey,
    setMspFeatureAsSeen,
    storageKey,
  ]);

  // Close handler:
  // - For first-time users: backdrop/escape clicks NEVER close; close button
  //   only works once every step has been visited.
  // - On first successful close, write onboarding_drawer_seen_once = true.
  const handleClose = async (
    _event?: object,
    reason?: "backdropClick" | "escapeKeyDown",
  ) => {
    if (isFirstTimeInDrawer) {
      if (reason === "backdropClick" || reason === "escapeKeyDown") return;
      if (!allVisited) return;
    }
    const allStepsComplete = completedStepKeys.size === DRAWER_STEPS.length;
    await performClose({ flipShowOnboardingFlag: allStepsComplete });
  };

  return (
    <Drawer
      anchor="left"
      open={open}
      onClose={handleClose}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: "rgba(0, 0, 0, 0.2)",
            backdropFilter: "blur(8px)",
            WebkitBackdropFilter: "blur(8px)",
          },
        },
      }}
      sx={{
        "& .MuiDrawer-paper": {
          width: "1058px",
          maxWidth: "100vw",
          backgroundColor: theme.palette.primitive.neutrals.neutral[900],
          flexDirection: "row",
          border: "none",
          overflow: "visible",
        },
      }}
    >
      <OnboardingDrawerSidebar
        activeStepKey={activeStepKey}
        completedStepKeys={completedStepKeys}
        unlockedStepKeys={unlockedStepKeys}
        onStepClick={handleStepClick}
        onMarkAllComplete={handleMarkAllComplete}
        isMarkingAll={isMarkingAll}
      />

      <Box sx={{ flex: 1 }} position="relative">
        <OnboardingDrawerContent
          activeStepKey={activeStepKey}
          isCompleted={completedStepKeys.has(activeStepKey)}
          isLastStep={
            DRAWER_STEPS[DRAWER_STEPS.length - 1].key === activeStepKey
          }
          isActiveStepRevisit={isActiveStepRevisit}
          onMarkComplete={handleMarkComplete}
          onSkip={handleSkip}
          isMarkingComplete={isCreateLoading}
        />
        {/* Close Button - hidden until first-time users have visited every step */}
        {!isCloseButtonDisabled && (
          <IconButton
            onClick={() => handleClose()}
            aria-label="Close onboarding drawer"
            sx={{
              position: "absolute",
              top: "8px",
              right: "-48px",
              zIndex: (t) => t.zIndex.drawer + 1,
              color: "#FFFFFF",
              backgroundColor: (t) =>
                t.palette.primitive.neutrals.neutral["900"],
              border: "1px solid #EDEDED1A",
              boxShadow: "0px 4px 16px rgba(0, 0, 0, 0.2)",
              width: "40px",
              height: "40px",
              borderRadius: "50%",
              "&:hover": {
                backgroundColor: (t) =>
                  t.palette.primitive.neutrals.neutral["800"],
              },
            }}
          >
            <CloseIcon sx={{ fontSize: "24px" }} />
          </IconButton>
        )}
      </Box>
    </Drawer>
  );
};
